import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Form, Button, Card, Alert, ListGroup } from 'react-bootstrap';
import './FormStyles.css';
import convertFileToWebP from "../../../utilities/convertFileToWebP";
import api from "../../../../src/seller-apis/api";
import ImageUploadGuideModal from './ImageUploadGuideModal';

export const ProductSpecifications = ({ 
  formData, 
  updateFormData, 
  validationErrors,
  onPrev,
  onPreview,
  onPublish,
  isPublishDisabled 
}) => {
  const [newSpecName, setNewSpecName] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [charCount, setCharCount] = useState({
    specName: 0,
    specValue: 0
  });
  const [uploadingPackageImage, setUploadingPackageImage] = useState({});
  const [showImageGuide, setShowImageGuide] = useState(false);
  
  // CRITICAL FIX: Add preview URLs state like ProductImages component
  const [packagePreviewUrls, setPackagePreviewUrls] = useState({});

  const vendorId = localStorage.getItem("vendorId") || "demo";

  // Refs for scrolling to validation errors
  const packageDetailsRef = useRef(null);

  // Auto-scroll to first validation error
  useEffect(() => {
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      if (packageDetailsRef.current) {
        packageDetailsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [validationErrors]);

  // CRITICAL FIX: Initialize preview URLs from formData on mount
  useEffect(() => {
    if (formData.packagePreviewUrls) {
      setPackagePreviewUrls(formData.packagePreviewUrls);
    }
  }, []);

  // CRITICAL FIX: Save preview URLs to formData whenever they change
  useEffect(() => {
    updateFormData('packagePreviewUrls', packagePreviewUrls);
  }, [packagePreviewUrls]);

  const handleSpecInputChange = (field, value, maxLength) => {
    if (value.length <= maxLength) {
      if (field === 'specName') {
        setNewSpecName(value);
        setCharCount(prev => ({ ...prev, specName: value.length }));
      } else if (field === 'specValue') {
        setNewSpecValue(value);
        setCharCount(prev => ({ ...prev, specValue: value.length }));
      }
    }
  };

  const addAdditionalSpec = () => {
    if (newSpecName && newSpecValue) {
      const newSpecs = [...(formData.additionalSpecs || []), { name: newSpecName, value: newSpecValue }];
      updateFormData('additionalSpecs', newSpecs);
      setNewSpecName('');
      setNewSpecValue('');
      setCharCount({ specName: 0, specValue: 0 });
    }
  };

  const removeAdditionalSpec = (index) => {
    const newSpecs = (formData.additionalSpecs || []).filter((_, i) => i !== index);
    updateFormData('additionalSpecs', newSpecs);
  };

  // Multiple Package Management
  const addPackageDetail = () => {
    const packages = formData.packages || [];
    packages.push({
      productName: '',
      weight: '',
      weightUnit: 'kg',
      length: '',
      width: '',
      height: '',
      dimensionUnit: 'cm',
      coverImage: '',
      image1: '',
      image2: '',
      image3: '',
      images: [],
      imageUrl: ''
    });
    updateFormData('packages', packages);
  };

  const removePackageDetail = (index) => {
    const packages = (formData.packages || []).filter((_, i) => i !== index);
    updateFormData('packages', packages);
    
    // CRITICAL FIX: Clean up preview URLs for removed package
    const newPreviewUrls = { ...packagePreviewUrls };
    ['coverImage', 'image1', 'image2', 'image3'].forEach(slot => {
      delete newPreviewUrls[`${index}-${slot}`];
    });
    setPackagePreviewUrls(newPreviewUrls);
  };

  const updatePackageDetail = (index, field, value) => {
    const packages = [...(formData.packages || [])];
    packages[index] = { ...packages[index], [field]: value };
    
    // Update images array when individual image slots change
    if (['coverImage', 'image1', 'image2', 'image3'].includes(field)) {
      const pkg = packages[index];
      const imagesList = [
        pkg.coverImage,
        pkg.image1,
        pkg.image2,
        pkg.image3
      ].filter(img => img);
      
      packages[index].images = imagesList;
      packages[index].imageUrl = pkg.coverImage || imagesList[0] || '';
    }
    
    updateFormData('packages', packages);
  };

  const handleDimensionUnitChange = (index, newUnit) => {
    const packages = [...(formData.packages || [])];
    const pkg = packages[index];
    const oldUnit = pkg.dimensionUnit;
    
    // If changing from ft to cm, convert all dimensions
    if (oldUnit === 'ft' && newUnit === 'cm') {
      const ftToCm = 30.48;
      
      if (pkg.length) {
        packages[index].length = (parseFloat(pkg.length) * ftToCm).toFixed(2);
      }
      if (pkg.width) {
        packages[index].width = (parseFloat(pkg.width) * ftToCm).toFixed(2);
      }
      if (pkg.height) {
        packages[index].height = (parseFloat(pkg.height) * ftToCm).toFixed(2);
      }
    }
    else if (oldUnit === 'cm' && newUnit === 'ft') {
      const cmToFt = 0.0328084;
      
      if (pkg.length) {
        packages[index].length = (parseFloat(pkg.length) * cmToFt).toFixed(2);
      }
      if (pkg.width) {
        packages[index].width = (parseFloat(pkg.width) * cmToFt).toFixed(2);
      }
      if (pkg.height) {
        packages[index].height = (parseFloat(pkg.height) * cmToFt).toFixed(2);
      }
    }
    
    packages[index].dimensionUnit = newUnit;
    updateFormData('packages', packages);
  };

  // CRITICAL FIX: Convert file to base64 for instant preview (same as ProductImages)
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // CRITICAL FIX: Updated package image upload with instant preview
  const handlePackageImageUpload = async (event, packageIndex, imageSlot) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (5MB limit)
  if (file.size > 5 * 1024 * 1024) {
    alert('Image size must be less than 5MB');
    return;
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select a valid image file');
    return;
  }

  const uploadKey = `${packageIndex}-${imageSlot}`;

  try {
    // Convert to base64 for INSTANT and PERSISTENT preview
    const base64Preview = await fileToBase64(file);
    setPackagePreviewUrls(prev => ({ ...prev, [uploadKey]: base64Preview }));
    
    // Set uploading state
    setUploadingPackageImage(prev => ({ ...prev, [uploadKey]: true }));
    
    // Convert to WebP
    const convertedImg = await convertFileToWebP(file);
    const str = convertedImg.name;
    const extension = convertedImg.name.slice(convertedImg.name.lastIndexOf("."));
    const comImg = str.replace(/[^\w]/g, "");
    const imageFinal = `package/${vendorId}_${Date.now()}_${comImg}${extension}`;

    const data = {
      imageName: `products/${imageFinal}`,
      type: "",
    };

    // Get signed URL for upload
    const response = await api.product.saveProductPackageImage(data);
    
    // ✅ FIX: Handle both response formats safely
    let uploadUrl, finalUrl;
    
    if (typeof response.data === 'string') {
      // Old format: data is directly the URL string
      uploadUrl = response.data;
      finalUrl = `https://s3.toolsvilla.com/products/${imageFinal}`;
    } else if (response.data && typeof response.data === 'object') {
      // New format: data is an object with uploadUrl and finalUrl
      uploadUrl = response.data.uploadUrl;
      finalUrl = response.data.finalUrl || `https://s3.toolsvilla.com/products/${imageFinal}`;
    } else {
      throw new Error('Invalid response format from server');
    }

    if (!uploadUrl) {
      throw new Error('Upload URL not received from server');
    }
    
    // Upload to S3 using uploadUrl
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: convertedImg,
      headers: {
        'Content-Type': convertedImg.type,
      },
    });

    if (uploadResponse.ok) {
      // Save only package/ prefix
      updatePackageDetail(packageIndex, imageSlot, imageFinal);
      console.log('Package image uploaded successfully:', finalUrl);
      
    } else {
      const errorText = await uploadResponse.text();
      console.error('Upload failed:', errorText);
      
      // Clear preview on error
      setPackagePreviewUrls(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[uploadKey];
        return newPreviews;
      });
      
      throw new Error(`Upload failed with status ${uploadResponse.status}`);
    }
  } catch (error) {
    console.error('Error uploading package image:', error);
    alert('Failed to upload image. Please try again.');
    
    // Clear preview on error
    setPackagePreviewUrls(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[uploadKey];
      return newPreviews;
    });
  } finally {
    // Clear uploading state
    setUploadingPackageImage(prev => ({ ...prev, [uploadKey]: false }));
  }
};


  const deletePackageImage = (packageIndex, imageSlot) => {
    // CRITICAL FIX: Clear both preview and S3 path
    const uploadKey = `${packageIndex}-${imageSlot}`;
    setPackagePreviewUrls(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[uploadKey];
      return newPreviews;
    });
    
    updatePackageDetail(packageIndex, imageSlot, '');
  };

  // CRITICAL FIX: Updated getImageUrl to prioritize base64 preview
  const getImageUrl = (imageName, packageIndex, imageSlot) => {
    // Priority 1: Use base64 preview if available (persists across tabs)
    const uploadKey = `${packageIndex}-${imageSlot}`;
    if (packagePreviewUrls[uploadKey]) {
      return packagePreviewUrls[uploadKey];
    }
    
    // Priority 2: Use S3 URL if we have the path
    if (!imageName) return null;
    imageName = imageName.trim();
    
    if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
      return imageName;
    }
    
    if (imageName.startsWith('products/')) {
      return `https://s3.toolsvilla.com/${imageName}`;
    }
    
    if (imageName.startsWith('package/')) {
      return `https://s3.toolsvilla.com/products/${imageName}`;
    }
    
    return `https://s3.toolsvilla.com/products/package/${imageName}`;
  };

  // Initialize packages with default units
  const packages = formData.packages?.length > 0 ? formData.packages : [
    {
      productName: '',
      weight: '',
      weightUnit: 'kg',
      length: '',
      width: '',
      height: '',
      dimensionUnit: 'cm',
      coverImage: '',
      image1: '',
      image2: '',
      image3: '',
      images: [],
      imageUrl: ''
    }
  ];

  // Check if package has validation errors
  const hasPackageError = (pkgIndex) => {
    return Object.keys(validationErrors).some(key => key.startsWith(`package${pkgIndex}_`));
  };

  // Get error for specific package field
  const getPackageError = (pkgIndex, fieldName) => {
    return validationErrors[`package${pkgIndex}_${fieldName}`];
  };

  // Check if any field in package is filled
  const hasAnyPackageField = (pkg) => {
    return !!(
      pkg.productName?.trim() ||
      pkg.weight ||
      pkg.length ||
      pkg.width ||
      pkg.height ||
      pkg.coverImage ||
      pkg.image1 ||
      pkg.image2 ||
      pkg.image3
    );
  };

  // Check if dimensions are incomplete (some filled but not all)
  const hasIncompleteDimensions = (pkg) => {
    const hasSome = !!(pkg.length || pkg.width || pkg.height);
    const hasAll = !!(pkg.length && pkg.width && pkg.height);
    return hasSome && !hasAll;
  };

  // Package Image Upload Slot Component
  const PackageImageUploadSlot = ({ label, imageState, packageIndex, imageSlot, isCover = false }) => {
    const uploadKey = `${packageIndex}-${imageSlot}`;
    const isUploading = uploadingPackageImage[uploadKey];
    
    // CRITICAL FIX: Check both preview URL and imageState
    const hasImage = imageState || packagePreviewUrls[uploadKey];
    const imageUrl = getImageUrl(imageState, packageIndex, imageSlot);

    return (
      <div className="package-image-upload-slot">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="custom-form-label small mb-0">
            {label} {isCover && <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>Cover</span>}
          </label>
          {hasImage && !isUploading && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => deletePackageImage(packageIndex, imageSlot)}
              style={{ padding: '2px 6px', fontSize: '0.7rem' }}
            >
              <i className="bi bi-trash"></i>
            </Button>
          )}
        </div>
        
        <Card className={`image-slot ${hasImage ? 'has-image' : 'empty-slot'}`} style={{ minHeight: '120px' }}>
          <Card.Body className="p-2 text-center position-relative d-flex align-items-center justify-content-center">
            {isUploading ? (
              <div className="upload-loader">
                <div className="spinner-border spinner-border-sm text-success mb-2" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
                <p className="text-muted mb-0 small">Uploading...</p>
              </div>
            ) : hasImage ? (
              <div className="uploaded-image w-100">
                <img 
                  src={imageUrl}
                  alt={label}
                  className="img-fluid rounded"
                  style={{ maxHeight: '100px', objectFit: 'cover', width: '100%' }}
                  onError={(e) => {
                    console.error('Image failed to load:', imageUrl);
                  }}
                />
              </div>
            ) : (
              <div className="upload-placeholder">
                <i className="bi bi-image text-muted" style={{ fontSize: '1.5rem' }}></i>
                <p className="text-muted small mb-2 mt-1">{label}</p>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePackageImageUpload(e, packageIndex, imageSlot)}
                  style={{ display: 'none' }}
                  id={`pkg-${packageIndex}-${imageSlot}-upload`}
                  disabled={isUploading}
                />
                <Button
                  as="label"
                  htmlFor={`pkg-${packageIndex}-${imageSlot}-upload`}
                  size="sm"
                  disabled={isUploading}
                  style={{
                    backgroundColor: '#d4edda',
                    borderColor: '#c3e6cb',
                    color: '#155724',
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                    cursor: 'pointer'
                  }}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-1"></i>
                      Choose
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  return (
    <div className="product-specifications-tab">
      <Form>
        {/* Package Details Section */}
        <Card ref={packageDetailsRef} className="mt-0 mb-1 border-0 bg-light">
          <Card.Body className="py-1 px-2">    
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-box text-primary me-2" style={{ fontSize: '1.25rem' }}></i>
                <h5 className="fw-semibold mb-0 text-dark">
                  Package Details 
                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75rem' }}>Optional</span>
                </h5>
              </div>
              <Button
                size="sm"
                onClick={addPackageDetail}
                style={{
                  borderColor: '#d4edda',
                  color: '#155724',
                  backgroundColor: 'transparent',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#d4edda';
                  e.currentTarget.style.borderColor = '#d4edda';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#d4edda';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> Add More Packages
              </Button>
            </div>

            {packages.map((pkg, pkgIndex) => {
              const hasAnyField = hasAnyPackageField(pkg);
              const incompleteDimensions = hasIncompleteDimensions(pkg);
              
              return (
                <Card 
                  key={pkgIndex} 
                  className={`mb-1 shadow-sm ${hasPackageError(pkgIndex) ? 'border-danger' : ''}`}
                >
                  <Card.Body className="py-1 px-2">    
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">
                        Package {pkgIndex + 1}
                        {hasPackageError(pkgIndex) && (
                          <span className="badge bg-danger ms-2" style={{ fontSize: '0.7rem' }}>
                            <i className="bi bi-exclamation-circle me-1"></i>
                            Has Errors
                          </span>
                        )}
                      </h6>
                      {packages.length > 1 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removePackageDetail(pkgIndex)}
                        >
                          <i className="bi bi-trash me-1"></i>
                        </Button>
                      )}
                    </div>

                    {/* Package Name */}
                    <Row className="mb-2 g-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Package Name
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={pkg.productName || ''}
                            onChange={(e) => updatePackageDetail(pkgIndex, 'productName', e.target.value)}
                            placeholder="e.g., Standard Box, Gift Wrap, Bulk Package"
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'productName') ? 'is-invalid' : ''}`}
                          />
                          {getPackageError(pkgIndex, 'productName') && (
                            <div className="invalid-feedback d-block">
                              {getPackageError(pkgIndex, 'productName')}
                            </div>
                          )}
                          {!pkg.productName?.trim() && (
                            <Form.Text className={hasAnyField ? 'text-danger' : 'text-muted'}>
                              {hasAnyField
                                ? 'Required when any package details are filled' 
                                : 'Give this package a descriptive name (optional)'}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Weight Information */}
                    <Row className="mb-2 g-3 align-items-start">
                      <Col md={9}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Weight (in Kilograms)
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={pkg.weight || ''}
                            onChange={(e) => updatePackageDetail(pkgIndex, 'weight', e.target.value)}
                            placeholder="Enter weight in kg"
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'weight') ? 'is-invalid' : ''}`}
                            onWheel={(e) => e.target.blur()}
                          />
                          {getPackageError(pkgIndex, 'weight') && (
                            <div className="invalid-feedback d-block">
                              {getPackageError(pkgIndex, 'weight')}
                            </div>
                          )}
                          {!pkg.weight && (
                            <Form.Text className={hasAnyField ? 'text-danger' : 'text-muted'}>
                              {hasAnyField
                                ? 'Required when any package details are filled' 
                                : 'Enter the actual weight of the product in kilograms (optional)'}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">Unit</Form.Label>
                          <Form.Control
                            disabled
                            value="Kilograms (kg)"
                            className="form-control-sm text-center"
                            style={{
                              backgroundColor: '#6c757d',
                              color: '#fff',
                              border: 'none',
                              fontWeight: 500
                            }}
                          />
                          <div style={{ height: '18px' }} />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Dimensions */}
                    <Row className="mb-2 g-3">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Length
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={pkg.length || ''}
                            onChange={(e) => updatePackageDetail(pkgIndex, 'length', e.target.value)}
                            placeholder="Length"
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'length') ? 'is-invalid' : ''}`}
                            onWheel={(e) => e.target.blur()}
                          />
                          {getPackageError(pkgIndex, 'length') && (
                            <div className="invalid-feedback d-block small">
                              {getPackageError(pkgIndex, 'length')}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Width
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={pkg.width || ''}
                            onChange={(e) => updatePackageDetail(pkgIndex, 'width', e.target.value)}
                            placeholder="Width"
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'width') ? 'is-invalid' : ''}`}
                            onWheel={(e) => e.target.blur()}
                          />
                          {getPackageError(pkgIndex, 'width') && (
                            <div className="invalid-feedback d-block small">
                              {getPackageError(pkgIndex, 'width')}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Height
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={pkg.height || ''}
                            onChange={(e) => updatePackageDetail(pkgIndex, 'height', e.target.value)}
                            placeholder="Height"
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'height') ? 'is-invalid' : ''}`}
                            onWheel={(e) => e.target.blur()}
                          />
                          {getPackageError(pkgIndex, 'height') && (
                            <div className="invalid-feedback d-block small">
                              {getPackageError(pkgIndex, 'height')}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="custom-form-label">
                            Dimension Unit
                            {hasAnyField && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Select
                            value={pkg.dimensionUnit || 'cm'}
                            onChange={(e) => handleDimensionUnitChange(pkgIndex, e.target.value)}
                            className={`form-control-sm custom-form-input ${getPackageError(pkgIndex, 'dimensionUnit') ? 'is-invalid' : ''}`}
                          >
                            <option value="cm">Centimeters (cm)</option>
                            <option value="ft">Feet (ft)</option>
                          </Form.Select>
                          {getPackageError(pkgIndex, 'dimensionUnit') && (
                            <div className="invalid-feedback d-block small">
                              {getPackageError(pkgIndex, 'dimensionUnit')}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    {(incompleteDimensions || (hasAnyField && !pkg.length && !pkg.width && !pkg.height)) && (
                      <Row className="mb-2">
                        <Col md={12}>
                          <Form.Text className="text-danger d-block" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            All dimension fields (Length, Width, Height) are required when any package details are filled
                          </Form.Text>
                        </Col>
                      </Row>
                    )}

                    {(pkg.length && pkg.width && pkg.height) && (
                      <Alert
                        variant="info"
                        className="mt-1 mb-2"
                        style={{ padding: '4px 8px', fontSize: '0.9rem' }}
                      >
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Package Size:</strong> {pkg.length} × {pkg.width} × {pkg.height} {pkg.dimensionUnit}
                      </Alert>
                    )}

                    {/* Package Images Section */}
                    <div className="package-images-section mt-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="custom-form-label mb-0">
                          <i className="bi bi-images me-2"></i>
                          Package Images (Optional - Up to 4 images)
                        </Form.Label>
                      </div>

                      <Row className="g-2">
                        <Col xs={6} md={3}>
                          <PackageImageUploadSlot
                            label="Image 1"
                            imageState={pkg.coverImage || ''}
                            packageIndex={pkgIndex}
                            imageSlot="coverImage"
                          />
                        </Col>
                        <Col xs={6} md={3}>
                          <PackageImageUploadSlot
                            label="Image 2"
                            imageState={pkg.image1 || ''}
                            packageIndex={pkgIndex}
                            imageSlot="image1"
                          />
                        </Col>
                        <Col xs={6} md={3}>
                          <PackageImageUploadSlot
                            label="Image 3"
                            imageState={pkg.image2 || ''}
                            packageIndex={pkgIndex}
                            imageSlot="image2"
                          />
                        </Col>
                        <Col xs={6} md={3}>
                          <PackageImageUploadSlot
                            label="Image 4"
                            imageState={pkg.image3 || ''}
                            packageIndex={pkgIndex}
                            imageSlot="image3"
                          />
                        </Col>
                      </Row>

                      <Form.Text className="text-muted d-block mt-2">
                        Upload images showing how the product is packaged. PNG, JPG, JPEG formats. Max 5MB per image.
                      </Form.Text>
                      <p 
                        className="image-upload-guide-link mt-1" 
                        onClick={() => setShowImageGuide(true)}
                        style={{ 
                          fontSize: '11px', 
                          color: 'red', 
                          cursor: 'pointer',
                          marginBottom: 0 
                        }}
                      >
                        <i className="bi bi-info-circle me-1"></i>
                        Image upload guidelines
                      </p>
                      <ImageUploadGuideModal 
                        show={showImageGuide} 
                        onHide={() => setShowImageGuide(false)} 
                      />
                    </div>
                  </Card.Body>
                </Card>
              );
            })}

            <Alert variant="info" className="mt-2 mb-0" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-info-circle me-2"></i>
              <strong>Package details are optional.</strong> However, if you fill any field (package name, weight, dimensions, or images), 
              all other package fields become mandatory to ensure complete information.
            </Alert>
          </Card.Body>
        </Card>

        {/* Navigation Buttons */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <Button 
            variant="secondary" 
            onClick={onPrev} 
            size="sm"
          >
            <i className="bi bi-arrow-left me-1"></i> Previous
          </Button>

          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onPreview}
              disabled={false}
            >
              <i className="bi bi-eye me-1"></i> Preview
            </Button>
            <Button
              size="sm"
              onClick={onPublish}
              disabled={false}
              style={{ 
                backgroundColor: '#165b5b',
                borderColor: '#165b5b',
                color: 'white',
                cursor: 'pointer',
                opacity: 1
              }}
            >
              <i className="bi bi-check-circle me-1"></i> Save & Publish
            </Button>
          </div>
        </div>
      </Form>

      <style jsx>{`
        .package-image-card {
          transition: transform 0.2s;
        }
        
        .package-image-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }

        .image-slot {
          min-height: 120px;
          transition: all 0.3s ease;
        }
        
        .image-slot.empty-slot {
          border: 2px dashed #dee2e6;
          background-color: #f8f9fa;
        }
        
        .image-slot.empty-slot:hover {
          border-color: #c3e6cb;
          background-color: #f0f8f5;
        }
        
        .image-slot.has-image {
          border: 2px solid #28a745;
        }

        .package-image-upload-slot {
          height: 100%;
        }

        .upload-placeholder {
          padding: 10px;
        }

        .upload-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .spinner-border-sm {
          width: 1.5rem;
          height: 1.5rem;
        }

        .is-invalid {
          border-color: #dc3545 !important;
        }

        .invalid-feedback {
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }

        .border-danger {
          border-color: #dc3545 !important;
          border-width: 2px !important;
        }
      `}</style>
    </div>
  );
};